import { Portal, Tooltip as ChakraTooltip } from '@chakra-ui/react';
import * as React from 'react';

import {
  tooltipDefaultOpenDelayMs,
  tooltipInteractiveCloseDelayMs,
  tooltipInteractiveOpenDelayMs,
} from './tooltip.kds';
import { useIsTouchDevice } from '../../hooks/use-touch-device';

export interface TooltipProps extends ChakraTooltip.RootProps {
  showArrow?: boolean;
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement | null>;
  content: React.ReactNode;
  contentProps?: ChakraTooltip.ContentProps;
  disabled?: boolean;
  /** Keeps the tooltip open while moving to clickable content inside (links, buttons). */
  interactive?: boolean;
}

const getTimingProps = (isTouchDevice: boolean, interactive?: boolean) => {
  if (isTouchDevice) {
    return { openDelay: 0, closeDelay: 0, closeOnPointerDown: false, closeOnEscape: true };
  }
  if (interactive) {
    return {
      openDelay: tooltipInteractiveOpenDelayMs,
      closeDelay: tooltipInteractiveCloseDelayMs,
      closeOnPointerDown: false,
      closeOnEscape: true,
    };
  }
  return { openDelay: tooltipDefaultOpenDelayMs, closeDelay: 0, closeOnPointerDown: undefined, closeOnEscape: true };
};

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(props, ref) {
  const {
    showArrow, children, disabled, portalled = true, content, contentProps, portalRef, interactive,
    openDelay: openDelayProp, closeDelay: closeDelayProp, closeOnPointerDown: closeOnPointerDownProp,
    open: openProp, onOpenChange: onOpenChangeProp, ...rest
  } = props;

  const isTouchDevice = useIsTouchDevice();
  const [isOpen, setIsOpen] = React.useState(false);
  const effectiveOpen = openProp ?? isOpen;
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const handledByManual = React.useRef(false);

  React.useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  React.useEffect(() => {
    if (!isTouchDevice || !effectiveOpen || disabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = triggerRef.current?.contains(target);
      const insideContent = contentRef.current?.contains(target);
      if (!insideTrigger && !insideContent) {
        if (openProp === undefined) setIsOpen(false);
        onOpenChangeProp?.({ open: false });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isTouchDevice, effectiveOpen, disabled]);

  if (disabled) {
    return children;
  }

  const timingProps = getTimingProps(isTouchDevice, interactive);
  const openDelay = openDelayProp ?? timingProps.openDelay;
  const closeDelay = closeDelayProp ?? timingProps.closeDelay;
  const closeOnPointerDown = closeOnPointerDownProp ?? timingProps.closeOnPointerDown;
  const { closeOnEscape } = timingProps;

  const tooltipContent = (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraTooltip.Positioner pointerEvents={interactive ? 'auto' : undefined}>
        <ChakraTooltip.Content ref={ref} {...contentProps}>
          {showArrow && (
            <ChakraTooltip.Arrow>
              <ChakraTooltip.ArrowTip />
            </ChakraTooltip.Arrow>
          )}
          {interactive ? <div ref={contentRef} style={{ pointerEvents: 'auto' }}>{content}</div> : content}
        </ChakraTooltip.Content>
      </ChakraTooltip.Positioner>
    </Portal>
  );

  if (isTouchDevice) {
    return (
      <ChakraTooltip.Root
        {...rest}
        open={effectiveOpen}
        onOpenChange={({ open }) => {
          if (handledByManual.current) {
            handledByManual.current = false;
            return;
          }
          if (openProp === undefined) setIsOpen(open);
          onOpenChangeProp?.({ open });
        }}
        openDelay={openDelay}
        closeDelay={closeDelay}
        closeOnPointerDown={closeOnPointerDown}
        closeOnEscape={closeOnEscape}
      >
        <ChakraTooltip.Trigger asChild>
          <span
            ref={triggerRef}
            role="none"
            onClick={e => {
              e.stopPropagation();
              handledByManual.current = true;
              if (openProp === undefined) setIsOpen(!effectiveOpen);
              onOpenChangeProp?.({ open: !effectiveOpen });
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handledByManual.current = true;
                if (openProp === undefined) setIsOpen(!effectiveOpen);
                onOpenChangeProp?.({ open: !effectiveOpen });
              }
            }}
            style={{ display: 'inline-flex' }}
          >
            {children}
          </span>
        </ChakraTooltip.Trigger>
        {tooltipContent}
      </ChakraTooltip.Root>
    );
  }

  return (
    <ChakraTooltip.Root
      {...rest}
      open={openProp}
      onOpenChange={onOpenChangeProp}
      openDelay={openDelay}
      closeDelay={closeDelay}
      closeOnPointerDown={closeOnPointerDown}
      closeOnEscape={closeOnEscape}
    >
      <ChakraTooltip.Trigger asChild>{children}</ChakraTooltip.Trigger>
      {tooltipContent}
    </ChakraTooltip.Root>
  );
});
